import { listOf, parseEntity } from '../entity/entity';
import { newUlid } from '../entity/catalog';
import { safeFilename } from '../board/newStoryline';
import { withList } from '../plan/edits';
import type { Arc, Status } from '../plan/arc';
import type { FileWrite } from '../vault/sparseWriter';

/** What the new-arc flow gathers. Only the name is required. */
export interface NewArc {
  name: string;
  /** Moved in from what was typed first, not drafted. */
  description: string;
  /** The recordings it comes out of, by path. */
  sources: string[];
  status: Status;
  startWith: string;
  doneWhen: string;
}

/** A front-matter value on one line, the way the vault's own arcs carry it. */
function oneLine(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * The new arc's file, in the shape the app's importer already mints from a
 * manifest-less file declaring `kind: arc`: the name as its `# ` title, the
 * description as its prose. Written with the id it will have. Null without a
 * name.
 */
export function newArcFile(arc: NewArc, id: string = newUlid()): FileWrite | null {
  const title = oneLine(arc.name);
  if (!title) return null;
  const fm = [`id: ${id}`, 'kind: arc', `status: ${arc.status}`, 'order: 0'];
  if (arc.sources.length > 0) fm.push('sources:', ...arc.sources.map((s) => `  - ${s}`));
  if (oneLine(arc.startWith)) fm.push(`start-with: ${oneLine(arc.startWith)}`);
  if (oneLine(arc.doneWhen)) fm.push(`done-when: ${oneLine(arc.doneWhen)}`);
  const prose = arc.description.trim();
  return {
    path: `arcs/${safeFilename(title)}.md`,
    text: `---\n${fm.join('\n')}\n---\n\n# ${title}\n${prose ? `\n${prose}\n` : ''}`,
  };
}

/** The nook's plan with [arcPath] held last — membership and order both. */
export function withArcHeld(nookRaw: string, nookPath: string, arcPath: string): string {
  const held = listOf(parseEntity(nookPath, nookRaw).frontMatter['arcs']);
  return withList(nookRaw, 'arcs', [...held, arcPath]);
}

/** True when [name] is already an arc's, by its name or by the file it would
 *  be written to. */
export function nameTaken(name: string, arcs: readonly Arc[]): boolean {
  const wanted = oneLine(name).toLowerCase();
  if (!wanted) return false;
  const path = `arcs/${safeFilename(oneLine(name))}.md`.toLowerCase();
  return arcs.some((a) => a.title.toLowerCase() === wanted || a.path.toLowerCase() === path);
}
